import React, {
  memo, useCallback, useMemo, useState,
} from 'react'

import { FiltersSidebar, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, object, string } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { buildFileFormatOptions } from './fileFormatOptions'

// "Blender (.blend)" -> name + parenthesised extensions.
const EXTENSION_PATTERN = /^(.*?)\s*(\([^)]*\))$/

const renderLabel = (label) => {
  const match = label.match(EXTENSION_PATTERN)
  if (!match) return label

  return (
    <span className="file-format-filter__label">
      <span>{match[1]}</span>
      {/* Keeps the accessible name "Blender (.blend)"; the flex gap renders the visual spacing. */}
      {' '}
      <span className="file-format-filter__extension">{match[2]}</span>
    </span>
  )
}

const isOptionSelected = (option, fileTypes) => option.ids.some(id => fileTypes.includes(id))

function FileFormatContent({
  fileTypes, onFilterChange, searchRef = null, limit = 8, hideShowMore = true, isListScrollable = false,
}) {
  const translate = useTranslate()

  const fileTypeOptions = useSearchPageStore(state => state.filtersOptions?.fileTypes)
  const options = useMemo(() => buildFileFormatOptions(fileTypeOptions?.filters), [fileTypeOptions])

  const selectedValues = useMemo(
    () => options.filter(option => isOptionSelected(option, fileTypes)).map(option => option.value),
    [options, fileTypes],
  )

  const trackFilterSelect = useTrackFilterSelect()

  // A merged option (Inventor, Lumion) toggles every id behind it; the store and URL keep ids.
  // Reports the backend label, like the legacy page's format badges, rather than the numeric id.
  const handleFiltersChange = useCallback((event, value, isOn) => {
    const option = options.find(candidate => candidate.value === String(value))

    if (isOn) trackFilterSelect(FILTER_NAME.FILE_FORMAT, option?.trackingLabel || value)
    const ids = option ? option.ids : [value]
    ids.forEach(id => onFilterChange(event, id, isOn))
  }, [options, onFilterChange, trackFilterSelect])

  const [query, setQuery] = useState('')
  // The drawer unmounts this on close, so a format past `limit` would come back hidden behind
  // "Show more" with nothing saying it is on. Start expanded when one is, and only on mount -
  // re-deriving would refuse to collapse while such a format is applied.
  const [showAll, setShowAll] = useState(() => !hideShowMore && options
    .slice(limit)
    .some(option => isOptionSelected(option, fileTypes)))

  // Search lives here instead of FiltersSidebar's built-in one: that lowercases each `label`,
  // so it can only match plain strings, while the rendered label is a node with a muted extension.
  // It also matches the backend titles, so the old names ("Autodesk FBX") still find a format.
  const filtersConfig = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const matching = normalized
      ? options.filter(option => option.searchText.includes(normalized))
      : options

    return [{
      ...fileTypeOptions,
      label: null,
      isSearchable: false,
      filters: matching.map(option => ({ value: option.value, label: renderLabel(option.label) })),
    }]
  }, [query, options, fileTypeOptions])

  // A search shows every match; limit=0 tells FiltersGroup to show all items.
  const isSearching = query.trim() !== ''
  const effectiveLimit = isSearching || (!hideShowMore && showAll) ? 0 : limit

  const list = (
    <FiltersSidebar
      isCollapsible={false}
      filters={filtersConfig}
      selectedFilters={selectedValues}
      onFiltersChange={handleFiltersChange}
      limit={effectiveLimit}
      isControlled
      hideLastDivider
      useNewCheckboxes
      hideShowMore
      baseFormWrapperClassName="filter-wrapper__content"
    />
  )

  return (
    <div className="file-format-filter__content">
      <FilterSearchInput
        id="file-format-search"
        value={query}
        onChange={setQuery}
        placeholder={translate('search_page.filters.file_format.search_placeholder')}
        ariaLabel={translate('search_page.filters.file_format.title')}
        inputRef={searchRef}
      />
      {/* The scroll box wraps the list alone: overflow-y: auto also clips on the x axis, so a
          search field inside it lost its 4px focus ring and sat under the scrollbar. */}
      {isListScrollable ? (
        <div className="file-format-filter__scrollable">{list}</div>
      ) : list}
      {!hideShowMore && !isSearching && !showAll && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(true)}
        >
          {translate('search_page.filters.file_format.show_more')}
        </Button>
      )}
      {!hideShowMore && !isSearching && showAll && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(false)}
        >
          {translate('search_page.filters.file_format.show_less')}
        </Button>
      )}
    </div>
  )
}

FileFormatContent.propTypes = {
  fileTypes: arrayOf(string).isRequired,
  onFilterChange: func.isRequired,
  searchRef: object,
  limit: number,
  hideShowMore: bool,
  isListScrollable: bool,
}

export default memo(FileFormatContent)
